import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { apiService } from '../services/api';
import { Vendor } from '../types';

interface CreateBusinessDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (vendor: Vendor) => void;
}

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];

const industries = [
  'Coffee Shop',
  'Restaurant',
  'Salon',
  'Gym',
  'Dentist',
  'Real Estate',
  'Retail',
  'Service Business',
  'Other',
];

export default function CreateBusinessDialog({
  open,
  onClose,
  onSuccess,
}: CreateBusinessDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    businessName: '',
    email: '',
    phone: '',
    industry: '',
    website: '',
    addressStreet: '',
    addressCity: '',
    addressState: '',
    addressZip: '',
  });

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
    setError(null);
  };

  const buildAddress = (): string | undefined => {
    const { addressStreet, addressCity, addressState, addressZip } = formData;
    const parts = [
      addressStreet.trim(),
      addressCity.trim(),
      [addressState.trim(), addressZip.trim()].filter(Boolean).join(' '),
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : undefined;
  };

  const handleSubmit = async () => {
    if (!formData.businessName.trim()) {
      setError('Business name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const vendor = await apiService.createVendor({
        businessName: formData.businessName.trim(),
        email: formData.email.trim() || undefined,
        phone: formData.phone.trim() || undefined,
        industry: formData.industry || undefined,
        website: formData.website.trim() || undefined,
        address: buildAddress(),
        subscriptionTier: 'free',
        // Phone number will be auto-provisioned (dedicated for all businesses)
      });

      // Set as selected vendor immediately
      sessionStorage.setItem('selectedVendorId', vendor.vendorId);
      localStorage.setItem('selectedVendorId', vendor.vendorId);
      
      onSuccess(vendor);
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create business');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        businessName: '',
        email: '',
        phone: '',
        industry: '',
        website: '',
        addressStreet: '',
        addressCity: '',
        addressState: '',
        addressZip: '',
      });
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon color="primary" />
          <Typography variant="h6">Create New Business</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Business Name"
            required
            fullWidth
            value={formData.businessName}
            onChange={handleChange('businessName')}
            placeholder="e.g., Morning Brew Coffee"
            disabled={loading}
          />
          <TextField
            label="Email"
            type="email"
            fullWidth
            value={formData.email}
            onChange={handleChange('email')}
            placeholder="business@example.com"
            disabled={loading}
            helperText="Optional - defaults to your account email"
          />
          <TextField
            label="Phone"
            fullWidth
            value={formData.phone}
            onChange={handleChange('phone')}
            placeholder="(555) 123-4567"
            disabled={loading}
          />
          <TextField
            label="Industry"
            select
            fullWidth
            value={formData.industry}
            onChange={handleChange('industry')}
            disabled={loading}
          >
            {industries.map((ind) => (
              <MenuItem key={ind} value={ind}>
                {ind}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Website"
            type="url"
            fullWidth
            value={formData.website}
            onChange={handleChange('website')}
            placeholder="https://example.com"
            disabled={loading}
          />
          <TextField
            label="Street Address"
            fullWidth
            value={formData.addressStreet}
            onChange={handleChange('addressStreet')}
            placeholder="123 Main St"
            disabled={loading}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="City"
              fullWidth
              value={formData.addressCity}
              onChange={handleChange('addressCity')}
              placeholder="Aurora"
              disabled={loading}
            />
            <TextField
              label="State"
              select
              sx={{ minWidth: 100 }}
              value={formData.addressState}
              onChange={handleChange('addressState')}
              disabled={loading}
            >
              <MenuItem value=""><em>—</em></MenuItem>
              {US_STATES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField
              label="ZIP"
              sx={{ width: 110 }}
              value={formData.addressZip}
              onChange={handleChange('addressZip')}
              placeholder="80015"
              disabled={loading}
              inputProps={{ maxLength: 10 }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.businessName.trim()}
          startIcon={loading ? <CircularProgress size={16} /> : <AddIcon />}
        >
          {loading ? 'Creating...' : 'Create Business'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


